/**
 * A password field you can look at.
 *
 * WHY THIS EXISTS. A password field is the only control on the platform that
 * refuses to show somebody what they typed, and the two places it hurts most
 * are the two places it is used: signing in, where a wrong character reads back
 * as "that did not sign you in" and sends somebody round the reset loop for a
 * typo, and setting a new password, where the field is asked for twice and the
 * confirmation can only tell you the two disagree, never which one is wrong.
 *
 * WHAT IT DOES NOT DO. It does not remember the reveal. Every mount starts
 * hidden, and switching between sign-in and set-a-password unmounts the field,
 * so a revealed password cannot survive into the next screen or the next
 * person's turn at the machine. There is no prop to start it revealed, because
 * the only caller who would want one is a caller who has decided somebody
 * else's screen is safe.
 *
 * The button is `type="button"`. Inside a form, a bare `<button>` submits, so
 * without this pressing the eye would attempt a sign-in with a half-typed
 * password and count against whatever rate limit is watching.
 */
import type { InputHTMLAttributes } from 'react'
import { forwardRef, useState } from 'react'
import { cn } from '../lib/cn'
import { FOCUS_RING } from './control-styles'
import { Input } from './Input'
import { IconEye, IconEyeOff } from './icons'
import { revealLabel, revealType } from './password-input-model'

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'type'> {
  invalid?: boolean
  inputSize?: 'sm' | 'md'
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ disabled, ...rest }, ref) {
    const [revealed, setRevealed] = useState(false)

    return (
      <Input
        ref={ref}
        {...rest}
        type={revealType(revealed)}
        disabled={disabled}
        trailing={
          <button
            type="button"
            // Icon-only, so it carries its own name. The icon set's own header
            // says an icon that is the whole of a control cannot supply one.
            aria-label={revealLabel(revealed)}
            aria-pressed={revealed}
            disabled={disabled}
            onClick={() => setRevealed((current) => !current)}
            className={cn(
              'grid h-6 w-6 place-items-center rounded text-ink-subtle transition [&>svg]:h-5 [&>svg]:w-5',
              'hover:text-ink-muted',
              FOCUS_RING,
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {revealed ? <IconEyeOff /> : <IconEye />}
          </button>
        }
      />
    )
  },
)
